import React, { useEffect, useMemo, useRef, useState } from 'react';
import AirRoundedIcon from '@mui/icons-material/AirRounded';
import ElectricBoltRoundedIcon from '@mui/icons-material/ElectricBoltRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import { STATIC_ICONS } from '../Constants';
import {
  Co2StatusIcon,
  HumidityStatusIcon,
  TemperatureStatusIcon,
} from '../svg/EnvironmentalStatusIcons';
import './EnvironmentalStatus.css';
import {
  ENVIRONMENTAL_STATUS_ACCENTS,
  getEnvironmentalAccent,
  inferEnvironmentalMetricKind,
  normalizeEnvironmentalIconType,
} from './environmentalStatusConfig';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const toFiniteNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const hasMeaningfulValue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
};

const formatMetricValue = (value, decimals = 0) => {
  if (!hasMeaningfulValue(value)) return '--';

  const numericValue = Number(value);
  const normalizedDecimals = Number(decimals);

  if (Number.isFinite(numericValue)) {
    if (Number.isFinite(normalizedDecimals)) {
      return Number(numericValue.toFixed(Math.max(0, normalizedDecimals))).toString();
    }
    return numericValue.toString();
  }

  return String(value);
};

const getDatastreamIconCandidate = (datastream) => {
  if (!datastream || typeof datastream !== 'object') return null;

  return (
    datastream.iconSvg ||
    datastream.icon ||
    datastream.iconUrl ||
    datastream.imageUrl ||
    datastream.image ||
    datastream.iconPath ||
    null
  );
};

const resolveIconUrl = (iconValue) => {
  if (typeof iconValue !== 'string') return '';
  const trimmed = iconValue.trim();
  if (!trimmed || trimmed.startsWith('<svg')) return '';
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('data:')
  ) {
    return trimmed;
  }
  if (trimmed.startsWith('icons/')) {
    return STATIC_ICONS + trimmed.replace(/^icons\//, '');
  }
  return STATIC_ICONS + trimmed;
};

const resolveMetricDatastream = ({ metric, devices, datastreams }) => {
  const rootDatastreams = Array.isArray(datastreams) ? datastreams.filter(Boolean) : [];
  const configuredDeviceId = Number(metric?.deviceId);
  let targetDatastreams = rootDatastreams;

  if (configuredDeviceId > 0) {
    const matchedDevice = (devices || []).find(
      (device) => Number(device?.id) === configuredDeviceId
    );

    if (matchedDevice) {
      targetDatastreams = (matchedDevice?.template?.datastreams || []).filter(Boolean);
    }
  }

  return (
    targetDatastreams.find((datastream) => {
      if (metric?.dataId !== null && metric?.dataId !== undefined && metric?.dataId !== '') {
        return Number(datastream?.id) === Number(metric.dataId);
      }

      if (metric?.pin) {
        return String(datastream?.pin || '') === String(metric.pin);
      }

      return false;
    }) || null
  );
};

const MetricIcon = ({ iconType, datastreamIcon, accentColor, label }) => {
  const normalizedIconType = normalizeEnvironmentalIconType(iconType);

  if (typeof datastreamIcon === 'string' && datastreamIcon.trim().startsWith('<svg')) {
    return (
      <span
        className="environmental-status-widget__iconInline"
        aria-label={label}
        dangerouslySetInnerHTML={{ __html: datastreamIcon }}
      />
    );
  }

  const iconUrl = resolveIconUrl(datastreamIcon);
  if (iconUrl) {
    return (
      <img
        className="environmental-status-widget__iconImage"
        src={iconUrl}
        alt={label}
        draggable={false}
      />
    );
  }

  if (normalizedIconType === 'temperature') {
    return <TemperatureStatusIcon className="environmental-status-widget__iconSvg" />;
  }

  if (normalizedIconType === 'humidity') {
    return <HumidityStatusIcon className="environmental-status-widget__iconSvg" />;
  }

  if (normalizedIconType === 'co2') {
    return <Co2StatusIcon className="environmental-status-widget__iconSvg" />;
  }

  const commonProps = {
    className: 'environmental-status-widget__fallbackIcon',
    style: { color: accentColor },
  };

  if (normalizedIconType === 'air') return <AirRoundedIcon {...commonProps} />;
  if (normalizedIconType === 'light') return <LightModeRoundedIcon {...commonProps} />;
  if (normalizedIconType === 'power') return <ElectricBoltRoundedIcon {...commonProps} />;

  return <SensorsRoundedIcon {...commonProps} />;
};

const EnvironmentalStatus = ({
  id,
  title,
  transparentBackground,
  draggable,
  dims,
  metrics,
  devices,
  datastreams,
  borderRadius = 24,
  titleSize = 20,
  datastreamTitleSize = 14,
  valueSize = 72,
}) => {
  const widgetRef = useRef(null);
  const [measuredDims, setMeasuredDims] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = widgetRef.current;
    if (!element) return undefined;

    const updateSize = (width, height) => {
      setMeasuredDims((prev) => {
        if (Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1) {
          return prev;
        }

        return { width, height };
      });
    };

    const rect = element.getBoundingClientRect();
    updateSize(rect.width || 0, rect.height || 0);

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        updateSize(width || 0, height || 0);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const resolvedMetrics = useMemo(
    () =>
      (Array.isArray(metrics) ? metrics : [])
        .map((metric, index) => {
          const safeMetric = metric || {};
          const datastream = resolveMetricDatastream({
            metric: safeMetric,
            devices,
            datastreams,
          });

          const inferredKind = inferEnvironmentalMetricKind(
            safeMetric?.label,
            datastream?.name,
            safeMetric?.unit,
            datastream?.unit,
            safeMetric?.iconType
          );

          const metricKind =
            safeMetric?.iconType && safeMetric.iconType !== 'auto'
              ? normalizeEnvironmentalIconType(safeMetric.iconType)
              : inferredKind;

          const accentColor =
            getEnvironmentalAccent(metricKind, datastream?.color) ||
            ENVIRONMENTAL_STATUS_ACCENTS.generic;

          const liveValue = hasMeaningfulValue(datastream?.value)
            ? datastream.value
            : safeMetric?.defaultValue ?? datastream?.defaultValue ?? '';

          const unit =
            String(safeMetric?.unit ?? '').trim() || String(datastream?.unit ?? '').trim();

          const label =
            String(safeMetric?.label ?? '').trim() ||
            String(datastream?.name ?? '').trim() ||
            `Datastream ${index + 1}`;

          return {
            id: safeMetric?.id || `environmental-metric-${index}`,
            enabled: typeof safeMetric?.enabled === 'boolean' ? safeMetric.enabled : true,
            label,
            unit,
            accentColor,
            value: formatMetricValue(liveValue, safeMetric?.decimals),
            iconType: metricKind,
            datastreamIcon: getDatastreamIconCandidate(datastream),
          };
        })
        .filter((metric) => metric.enabled),
    [datastreams, devices, metrics]
  );

  const widgetDims = useMemo(() => {
    const liveWidth =
      Number.isFinite(measuredDims.width) && measuredDims.width > 0
        ? measuredDims.width
        : Number(dims?.width) || 0;
    const liveHeight =
      Number.isFinite(measuredDims.height) && measuredDims.height > 0
        ? measuredDims.height
        : Number(dims?.height) || 0;

    const width = Math.max(160, liveWidth || 420);
    const height = Math.max(130, liveHeight || 210);
    const metricCount = Math.max(resolvedMetrics.length, 1);
    const safeTitleSize = Math.max(12, toFiniteNumber(titleSize, 20));
    const safeDatastreamTitleSize = Math.max(10, toFiniteNumber(datastreamTitleSize, 14));
    const safeValueSize = Math.max(18, toFiniteNumber(valueSize, 72));
    const safeBorderRadius = Math.max(0, toFiniteNumber(borderRadius, 24));

    const isTiny = width < 320 || height < 185;
    const isCompact = width < 500 || height < 235;
    const isMedium = width < 720 || height < 300;

    let columns = metricCount;
    if (metricCount <= 1) {
      columns = 1;
    } else if (isTiny) {
      columns = width >= 260 && height >= 200 ? Math.min(2, metricCount) : 1;
    } else if (isCompact) {
      columns = Math.min(2, metricCount);
    } else if (isMedium) {
      columns = Math.min(3, metricCount);
    } else {
      columns = Math.min(3, metricCount);
    }

    const gap = isTiny ? 8 : isCompact ? 10 : isMedium ? 12 : 16;
    const paddingX = isTiny ? 10 : isCompact ? 12 : isMedium ? 16 : 22;
    const paddingY = isTiny ? 9 : isCompact ? 11 : isMedium ? 14 : 18;
    const topbarGap = Math.round(
      clamp(
        safeTitleSize * (isTiny ? 0.38 : 0.46),
        isTiny ? 8 : 10,
        18
      )
    );
    const titleFontSize = Math.round(
      clamp(
        safeTitleSize * (isTiny ? 0.76 : isCompact ? 0.86 : isMedium ? 0.98 : 1.08),
        12,
        120
      )
    );
    const rows = Math.max(1, Math.ceil(metricCount / columns));
    const topbarHeight = Math.max(Math.round(titleFontSize * 1.22), isTiny ? 20 : 24);
    const availableMetricsHeight = Math.max(
      72,
      height - (paddingY * 2) - topbarHeight - topbarGap
    );
    const metricCellHeight = Math.max(
      isTiny ? 52 : 60,
      (availableMetricsHeight - (gap * Math.max(0, rows - 1))) / rows
    );
    const perColumnWidth = Math.max(80, (width - (paddingX * 2) - (gap * (columns - 1))) / columns);
    const labelFontSize = Math.round(
      clamp(
        safeDatastreamTitleSize * (isTiny ? 0.82 : isCompact ? 0.92 : isMedium ? 0.98 : 1.02),
        10,
        Math.max(12, Math.min(perColumnWidth * 0.15, 22))
      )
    );
    const bubbleSize = Math.round(
      clamp(
        Math.max(
          labelFontSize * (isTiny ? 1.7 : 1.9),
          Math.min(perColumnWidth * (isTiny ? 0.16 : 0.18), metricCellHeight * 0.36)
        ),
        Math.max(22, labelFontSize * 1.55),
        Math.min(isTiny ? 34 : isCompact ? 40 : 46, metricCellHeight * 0.52, perColumnWidth * 0.28)
      )
    );
    const iconSize = Math.round(
      clamp(
        Math.max(labelFontSize * 1.12, bubbleSize * (isTiny ? 0.64 : 0.68)),
        15,
        bubbleSize - (isTiny ? 7 : 8)
      )
    );
    const labelToIconGap = Math.round(clamp(labelFontSize * 0.5, 6, 14));
    const maxValueByWidth = perColumnWidth * (isTiny ? 0.32 : isCompact ? 0.38 : 0.42);
    const metricTopGap = Math.round(
      clamp(
        labelFontSize * (isTiny ? 0.28 : 0.36),
        4,
        12
      )
    );
    const labelHeight = Math.round(
      clamp(
        Math.max(labelFontSize * (isTiny ? 2 : 2.15), bubbleSize * 0.88),
        24,
        isTiny ? 40 : 46
      )
    );
    const availableValueHeight = Math.max(
      22,
      metricCellHeight - labelHeight - metricTopGap
    );
    const rowDensityScale = rows <= 1 ? 1 : rows === 2 ? 0.84 : 0.68;
    const maxValueByHeight = Math.max(
      24,
      availableValueHeight * (isTiny ? 0.92 : 0.98)
    );
    const valueFontSize = Math.round(
      clamp(
        safeValueSize * (isTiny ? 0.5 : isCompact ? 0.62 : isMedium ? 0.74 : 0.82) * rowDensityScale,
        16,
        Math.max(22, Math.min(maxValueByWidth, maxValueByHeight, 160))
      )
    );
    const unitFontSize = Math.round(
      clamp(
        Math.min(valueFontSize * 0.36, availableValueHeight * 0.42),
        11,
        28
      )
    );
    const cardRadius = Math.round(clamp(safeBorderRadius, 0, 64));
    const surfaceRadius = Math.round(clamp(safeBorderRadius * 0.5, 0, 32));
    return {
      width,
      height,
      isTiny,
      isCompact,
      columns,
      rows,
      cardRadius: `${cardRadius}px`,
      surfaceRadius: `${surfaceRadius}px`,
      paddingX: `${paddingX}px`,
      paddingY: `${paddingY}px`,
      topbarGap: `${topbarGap}px`,
      gridGap: `${gap}px`,
      titleFontSize: `${titleFontSize}px`,
      titleMaxWidth: '100%',
      metricPaddingX: isTiny ? '0px' : isCompact ? '4px' : '8px',
      dividerInset: isTiny ? '4px' : '8px',
      labelToIconGap: `${labelToIconGap}px`,
      labelFontSize: `${labelFontSize}px`,
      labelHeight: `${labelHeight}px`,
      labelPaddingX: isTiny ? '10px' : '12px',
      labelPaddingY: isTiny ? '5px' : '6px',
      bubbleSize: `${bubbleSize}px`,
      iconSize: `${iconSize}px`,
      valueFontSize: `${valueFontSize}px`,
      unitFontSize: `${unitFontSize}px`,
      metricTopGap: `${metricTopGap}px`,
      metricValueGap: isTiny ? '2px' : '4px',
    };
  }, [
    borderRadius,
    datastreamTitleSize,
    dims?.width,
    dims?.height,
    measuredDims.height,
    measuredDims.width,
    resolvedMetrics.length,
    titleSize,
    valueSize,
  ]);

  const widgetTitle = String(title || '').trim() || 'Environmental Status';
  const hasMetrics = resolvedMetrics.length > 0;
  const showDividers = widgetDims.columns > 1 && !widgetDims.isTiny;
  const metricLayoutSpans = useMemo(() => {
    const metricCount = resolvedMetrics.length;
    const columnCount = Math.max(1, Number(widgetDims.columns) || 1);
    const spans = Array(metricCount).fill(1);

    if (metricCount === 0 || columnCount <= 1) {
      return spans;
    }

    const lastRowCount = metricCount % columnCount;
    if (lastRowCount === 0) {
      return spans;
    }

    const lastRowStart = metricCount - lastRowCount;
    let remainingSlots = columnCount;
    let remainingItems = lastRowCount;

    for (let index = lastRowStart; index < metricCount; index += 1) {
      const span = Math.max(1, Math.ceil(remainingSlots / remainingItems));
      spans[index] = span;
      remainingSlots -= span;
      remainingItems -= 1;
    }

    return spans;
  }, [resolvedMetrics.length, widgetDims.columns]);

  return (
    <div
      ref={widgetRef}
      className={[
        'widget-container',
        'environmental-status-widget',
        transparentBackground ? 'is-transparent' : '',
        widgetDims.isTiny ? 'environmental-status-widget--tiny' : '',
        widgetDims.isCompact ? 'environmental-status-widget--compact' : '',
      ].filter(Boolean).join(' ')}
      draggable={draggable}
      onMouseDown={(e) => {
        if (draggable) e.currentTarget.style.cursor = 'grabbing';
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.cursor = draggable ? 'grab' : 'default';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.cursor = draggable ? 'grab' : 'default';
      }}
      onDragStart={(e) => {
        const widgetWrapper = e.currentTarget.closest('.widget');
        const widgetId = id || (widgetWrapper ? widgetWrapper.id : '');
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        let dragData = `environmental-status;${widgetId};${offsetX};${offsetY};`;
        dragData += widgetId.startsWith('base') ? 'add' : 'move';
        e.dataTransfer.setData('text/plain', dragData);
      }}
      style={{
        cursor: draggable ? 'grab' : 'default',
        borderRadius: '0px',
        '--esw-card-radius': '0px',
        '--esw-surface-radius': widgetDims.surfaceRadius,
        '--esw-padding-x': widgetDims.paddingX,
        '--esw-padding-y': widgetDims.paddingY,
        '--esw-topbar-gap': widgetDims.topbarGap,
        '--esw-grid-gap': widgetDims.gridGap,
        '--esw-title-size': widgetDims.titleFontSize,
        '--esw-title-max-width': widgetDims.titleMaxWidth,
        '--esw-columns': widgetDims.columns,
        '--esw-metric-padding-x': widgetDims.metricPaddingX,
        '--esw-divider-inset': widgetDims.dividerInset,
        '--esw-label-to-icon-gap': widgetDims.labelToIconGap,
        '--esw-label-size': widgetDims.labelFontSize,
        '--esw-label-height': widgetDims.labelHeight,
        '--esw-label-padding-x': widgetDims.labelPaddingX,
        '--esw-label-padding-y': widgetDims.labelPaddingY,
        '--esw-bubble-size': widgetDims.bubbleSize,
        '--esw-icon-size': widgetDims.iconSize,
        '--esw-value-size': widgetDims.valueFontSize,
        '--esw-unit-size': widgetDims.unitFontSize,
        '--esw-metric-top-gap': widgetDims.metricTopGap,
        '--esw-metric-value-gap': widgetDims.metricValueGap,
      }}
    >
      <div className="environmental-status-widget__inner">
        <div className="environmental-status-widget__topbar">
          <h3 className="environmental-status-widget__title">{widgetTitle}</h3>
        </div>

        {hasMetrics ? (
          <div className="environmental-status-widget__metricsRow">
            {resolvedMetrics.map((metric, index) => (
              <section
                key={metric.id}
                className={[
                  'environmental-status-widget__metric',
                  showDividers && index > 0 && (metricLayoutSpans[index] || 1) < widgetDims.columns
                    ? 'has-divider'
                    : '',
                ].join(' ')}
                style={{
                  gridColumn: `span ${metricLayoutSpans[index] || 1}`,
                }}
              >
                <div className="environmental-status-widget__metricTop">
                  <span
                    className="environmental-status-widget__metricLabel"
                    style={{
                      '--esw-accent': metric.accentColor,
                      '--esw-accent-soft': `${metric.accentColor}26`,
                    }}
                    title={metric.label}
                  >
                    {metric.label}
                  </span>

                  <span
                    className="environmental-status-widget__metricIconBubble"
                    style={{
                      '--esw-accent': metric.accentColor,
                      '--esw-accent-soft': `${metric.accentColor}16`,
                      '--esw-accent-shadow': `${metric.accentColor}55`,
                    }}
                  >
                    <MetricIcon
                      iconType={metric.iconType}
                      datastreamIcon={metric.datastreamIcon}
                      accentColor={metric.accentColor}
                      label={metric.label}
                    />
                  </span>
                </div>

                <div className="environmental-status-widget__metricValueWrap">
                  <span className="environmental-status-widget__metricValue">
                    {metric.value}
                  </span>

                  {metric.unit ? (
                    <span
                      className="environmental-status-widget__metricUnit"
                      style={{ color: metric.accentColor }}
                    >
                      {metric.unit}
                    </span>
                  ) : null}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="environmental-status-widget__empty">
            Add a datastream to show environmental status.
          </div>
        )}
      </div>
    </div>
  );
};

export default EnvironmentalStatus;
